import { TradeType } from '@services/appsync/types';
import React from 'react';
import { ActivityRecord } from '../types';
import { amountFmt, dateFmt } from '../util';
import { RecordView } from './RecordView';

type TradeCardProps = {
  record: Extract<ActivityRecord, { type: 'Trade' }>;
};

export const TradeCard: React.FC<TradeCardProps> = ({ record }) => {
  const { type } = record.dataType;
  const data: [label: string, value: string][] = [];

  const title =
    {
      NA: 'n/a',
      PURCHASE: 'Investment',
      PURCHASE_AUTO_INVEST: 'Auto Investment',
      PURCHASE_DIVIDEND_REINVESTMENT: 'Dividend Reinvestment',
      REDEMPTION: 'Redemption',
    }[
      record.dataType.type === TradeType.REDEMPTION
        ? 'REDEMPTION'
        : record.dataType.type === TradeType.PURCHASE && record.dataType.auto_invest
        ? 'PURCHASE_AUTO_INVEST'
        : record.dataType.type === TradeType.PURCHASE && record.dataType.is_drip
        ? 'PURCHASE_DIVIDEND_REINVESTMENT'
        : record.dataType.type === TradeType.PURCHASE
        ? 'PURCHASE'
        : 'NA'
    ] ?? 'n/a';

  const status = {
    CANCELED: 'Canceled',
    CREATED: 'Pending',
    FAILED: 'Failed',
    FUNDED: 'Pending',
    NA: 'n/a',
    OPENED: 'Pending',
    PENDING_CANCEL: 'Cancelation Processing',
    PENDING_FUNDING: 'Transfer Pending',
    SCHEDULED: 'Pending',
    SETTLED: 'Completed',
  }[record.dataType?.status ?? 'NA'];

  const date = {
    CANCELED: record.dataType?.created_at ?? null,
    CREATED: record.dataType?.created_at ?? null,
    FAILED: record.dataType?.created_at ?? null,
    FUNDED: record.dataType?.opened_at ?? null,
    NA: null,
    OPENED: record.dataType?.opened_at ?? null,
    PENDING_CANCEL: record.dataType?.created_at ?? null,
    PENDING_FUNDING: record.dataType?.opened_at ?? null,
    SCHEDULED: record.dataType?.scheduled_closing_at ?? null,
    SETTLED: record.dataType?.settled_at ?? null,
  }[record.dataType?.status ?? 'NA'];

  switch (type) {
    case TradeType.PURCHASE:
      data.push(['Date', dateFmt(date ?? '')]);
      data.push(['Amount', amountFmt(record.dataType.principal ?? 0)]);
      data.push(['Status', status]);
      data.push(['Type', title]);
      if (record.dataType.subscription_offer?.display_name)
        data.push(['Fund', record.dataType.subscription_offer?.display_name]);
      break;
    case TradeType.REDEMPTION:
      data.push(['Date', dateFmt(record.dataType.redemption?.requested_date ?? '')]);
      data.push(['Amount', amountFmt(record.dataType.redemption?.requested_amount ?? 0)]);
      data.push(['Status', status]);
      data.push(['Type', title]);
      break;
  }

  return (
    <RecordView
      dateLabel={dateFmt(date ?? '')}
      activityName={title}
      activityDesc={record.dataType.subscription_offer?.display_name ?? ''}
      status={status}
      amount={amountFmt(record.dataType.principal ?? 0)}
      data={data}
      assetClass={record.dataType.subscription_offer?.asset_class}
    />
  );
};
